/**
 * 快捷命令集合
 * 提供更便捷的命令行体验
 */

import { Command } from 'commander';
import { WorkflowManager } from '../core/workflow-manager';
import { TaskStore } from '../core/task-store';
import { TagManager } from '../core/tag-manager';
import { ComplexityAnalyzer } from '../core/complexity-analyzer';
import { logger } from '../utils/logger';
import { withErrorHandling } from '../utils/error-handler';
import { colors } from '../utils/colors';
import { formatDuration } from '../utils/formatters';
import inquirer from 'inquirer';
import { Table } from 'console-table-printer';
import path from 'path';
import { Task, TaskPriority } from '../types';

/**
 * 创建 next 命令 - 智能开始下一个任务
 */
export function createNextCommand(): Command {
  const command = new Command('next');
  
  command
    .description('Start the next recommended task')
    .alias('nx')
    .option('-a, --auto', 'Auto-start without confirmation', false)
    .option('-t, --tags <tags>', 'Filter by tags')
    .option('-p, --priority <priority>', 'Filter by priority')
    .action(withErrorHandling(async (options) => {
      const manager = new WorkflowManager();
      const taskStore = new TaskStore();
      const tagManager = new TagManager(path.join(process.cwd(), '.workflow'));
      
      await taskStore.initialize();
      await tagManager.initialize();
      
      // 检查是否有当前任务
      const currentState = await manager.getTaskState();
      if (currentState?.current) {
        console.log(colors.warning('⚠️  You have an active task:'));
        console.log(colors.cyan(currentState.current.task));
        
        if (!options.auto) {
          const { action } = await inquirer.prompt([{
            type: 'list',
            name: 'action',
            message: 'What would you like to do?',
            choices: [
              { name: 'Complete current task', value: 'complete' },
              { name: 'Skip to next task', value: 'skip' },
              { name: 'Continue current task', value: 'continue' }
            ]
          }]);
          
          if (action === 'complete') {
            await manager.completeCurrentTask();
            console.log(colors.success('✅ Task completed'));
          } else if (action === 'continue') {
            return;
          }
        }
      }
      
      // 获取推荐的下一个任务
      let tasks = await taskStore.getTasks();
      
      // 过滤待处理任务
      tasks = tasks.filter(t => t.status === 'pending');
      
      // 按标签过滤
      if (options.tags) {
        const filterTags = options.tags.split(',').map((t: string) => t.trim());
        tasks = tagManager.filterByTags(tasks, filterTags);
      }
      
      // 按优先级过滤
      if (options.priority) {
        tasks = tasks.filter(t => t.priority === options.priority);
      }
      
      if (tasks.length === 0) {
        console.log(colors.success('🎉 No pending tasks! Great job!'));
        return;
      }
      
      // 智能排序：优先级 > 标签匹配度 > 创建时间
      tasks.sort((a, b) => {
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
        if (priorityDiff !== 0) return priorityDiff;
        
        // 按创建时间排序
        return (a.createdAt?.getTime() || 0) - (b.createdAt?.getTime() || 0);
      });
      
      const nextTask = tasks[0];
      
      // 显示任务信息
      console.log(colors.header('\n📌 Next Recommended Task:\n'));
      console.log(colors.cyan(nextTask.content));
      console.log(`Priority: ${colors.priority[nextTask.priority](nextTask.priority.toUpperCase())}`);
      if (nextTask.tags && nextTask.tags.length > 0) {
        console.log(`Tags: ${nextTask.tags.join(', ')}`);
      }
      
      // 分析复杂度
      const analyzer = new ComplexityAnalyzer();
      const analysis = await analyzer.analyzeComplexity(nextTask);
      console.log(`Complexity: ${getComplexityColor(analysis.score)}(${analysis.score}/100)`);
      console.log(`Estimated time: ${colors.highlight(formatDuration(analysis.estimatedHours * 60 * 60 * 1000))}`);
      console.log();
      
      // 确认开始
      if (!options.auto) {
        const { start } = await inquirer.prompt([{
          type: 'confirm',
          name: 'start',
          message: 'Start this task?',
          default: true
        }]);
        
        if (!start) {
          console.log(colors.dim('Task not started'));
          return;
        }
      }
      
      // 开始任务
      await manager.startTask({
        task: nextTask.content,
        priority: nextTask.priority,
        phase: 'understanding'
      });
      
      console.log(colors.success('✅ Task started successfully!'));
      console.log(colors.dim('Use "workflow done" when finished or "workflow now" to check status'));
    }));
  
  return command;
}

/**
 * 创建 done 命令 - 快速完成当前任务
 */
export function createDoneCommand(): Command {
  const command = new Command('done');
  
  command
    .description('Complete the current task')
    .alias('dn')
    .option('-n, --notes <notes>', 'Add completion notes')
    .option('-s, --start-next', 'Start next task immediately', false)
    .action(withErrorHandling(async (options) => {
      const manager = new WorkflowManager();
      const state = await manager.getTaskState();
      
      if (!state?.current) {
        console.log(colors.warning('No active task to complete'));
        console.log(colors.dim('Use "workflow next" to start a task'));
        return;
      }
      
      // 显示任务信息
      console.log(colors.header('Completing task:'));
      console.log(colors.cyan(state.current.task));
      
      const duration = Date.now() - new Date(state.current.startTime).getTime();
      const minutes = Math.floor(duration / 60000);
      console.log(`Duration: ${formatDuration(duration)}`);
      
      // 获取完成备注
      let notes = options.notes;
      if (!notes) {
        const { inputNotes } = await inquirer.prompt([{
          type: 'input',
          name: 'inputNotes',
          message: 'Completion notes (optional):',
          default: ''
        }]);
        notes = inputNotes;
      }
      
      // 完成任务
      const result = await manager.completeCurrentTask(notes);
      
      if (result.success) {
        console.log(colors.success('\n✅ Task completed successfully!'));
        
        // 更新 TaskStore 中的任务状态
        const taskStore = new TaskStore();
        await taskStore.initialize();
        const tasks = await taskStore.getTasks();
        const task = tasks.find(t => t.content === state.current.task);
        if (task) {
          await taskStore.updateTask(task.id, {
            status: 'completed',
            completedAt: new Date()
          });
        }
        
        // 显示统计
        const todayCompleted = state.history.filter(h => {
          const today = new Date();
          const taskDate = new Date(h.endTime);
          return taskDate.toDateString() === today.toDateString() && h.status === 'completed';
        }).length + 1;
        
        console.log(colors.dim(`Tasks completed today: ${todayCompleted}`));
        
        // 自动开始下一个任务
        if (options.startNext) {
          console.log(colors.header('\n🔄 Starting next task...\n'));
          const nextCmd = createNextCommand();
          await nextCmd.parseAsync(['next', '--auto'], { from: 'user' });
        } else {
          // 获取推荐
          const recommendations = await manager.recommendNextTask();
          if (recommendations && recommendations.length > 0) {
            console.log(colors.header('\n🎯 Next recommended task:'));
            const next = recommendations[0];
            console.log(colors.cyan(next.task));
            console.log(colors.dim('Use "workflow next" to start'));
          }
        }
      } else {
        console.log(colors.error(`Failed to complete task: ${result.error}`));
      }
    }));
  
  return command;
}

/**
 * 创建 now 命令 - 显示当前任务状态
 */
export function createNowCommand(): Command {
  const command = new Command('now');
  
  command
    .description('Show current task status')
    .option('-d, --detailed', 'Show detailed information', false)
    .action(withErrorHandling(async (options) => {
      const manager = new WorkflowManager();
      const state = await manager.getTaskState();
      
      if (!state?.current) {
        console.log(colors.warning('No active task'));
        console.log(colors.dim('Use "workflow next" to start a task'));
        return;
      }
      
      // 基本信息
      console.log(colors.header('\n📋 Current Task\n'));
      console.log(colors.cyan(state.current.task));
      console.log();
      
      const table = new Table({
        columns: [
          { name: 'field', title: 'Field', alignment: 'left' },
          { name: 'value', title: 'Value', alignment: 'left' }
        ]
      });
      
      // 计算持续时间
      const duration = Date.now() - new Date(state.current.startTime).getTime();
      const minutes = Math.floor(duration / 60000);
      
      table.addRow({ field: 'Status', value: '🔄 In Progress' });
      table.addRow({ field: 'Phase', value: state.current.phase });
      table.addRow({ field: 'Priority', value: state.current.priority.toUpperCase() });
      table.addRow({ field: 'Started', value: new Date(state.current.startTime).toLocaleTimeString() });
      table.addRow({ field: 'Duration', value: formatDuration(duration) });
      
      table.printTable();
      
      // 详细信息
      if (options.detailed) {
        // 获取任务详情
        const taskStore = new TaskStore();
        await taskStore.initialize();
        const tasks = await taskStore.getTasks();
        const task = tasks.find(t => t.content === state.current.task);
        
        if (task) {
          console.log(colors.header('\n📊 Task Details:\n'));
          
          if (task.tags && task.tags.length > 0) {
            console.log(`Tags: ${task.tags.map(t => colors.cyan(t)).join(', ')}`);
          }
          
          // 分析复杂度
          const analyzer = new ComplexityAnalyzer();
          const analysis = await analyzer.analyzeComplexity(task);
          
          console.log(`Complexity: ${getComplexityColor(analysis.score)}(${analysis.score}/100)`);
          console.log(`Estimated remaining: ${colors.highlight(formatDuration(Math.max(0, (analysis.estimatedHours * 60 * 60 * 1000) - duration)))}`);
          console.log(`Confidence: ${Math.round(analysis.confidence * 100)}%`);
          
          if (analysis.risks.length > 0) {
            console.log(colors.header('\n⚠️  Risks:'));
            analysis.risks.forEach(risk => {
              console.log(colors.warning(`• ${risk}`));
            });
          }
        }
      }
      
      // 快捷操作提示
      console.log(colors.header('\n⚡ Quick Actions:'));
      console.log('• Complete task: ' + colors.command('workflow done'));
      console.log('• Skip to next: ' + colors.command('workflow skip'));
      console.log('• Update phase: ' + colors.command('workflow task phase <phase>'));
    }));
  
  return command;
}

/**
 * 创建 today 命令 - 显示今日任务统计
 */
export function createTodayCommand(): Command {
  const command = new Command('today');
  
  command
    .description('Show today\'s task summary')
    .option('-v, --verbose', 'Show detailed task list', false)
    .action(withErrorHandling(async (options) => {
      const manager = new WorkflowManager();
      const taskStore = new TaskStore();
      await taskStore.initialize();
      
      const state = await manager.getTaskState();
      const tasks = await taskStore.getTasks();
      const today = new Date();
      
      // 统计今日数据
      const todayTasks = state?.history.filter(h => {
        const taskDate = new Date(h.endTime);
        return taskDate.toDateString() === today.toDateString();
      }) || [];
      
      const completedToday = todayTasks.filter(t => t.status === 'completed').length;
      const failedToday = todayTasks.filter(t => t.status === 'failed').length;
      const totalTimeToday = todayTasks.reduce((sum, t) => sum + t.duration, 0); // milliseconds
      
      // 待处理任务统计
      const pendingTasks = tasks.filter(t => t.status === 'pending');
      const highPriorityPending = pendingTasks.filter(t => t.priority === 'high').length;
      
      // 显示仪表板
      console.log(colors.header('\n📊 Today\'s Dashboard\n'));
      console.log(colors.bold(today.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })));
      console.log();
      
      // 统计卡片
      const statsTable = new Table({
        columns: [
          { name: 'metric', title: 'Metric', alignment: 'left' },
          { name: 'value', title: 'Value', alignment: 'center' },
          { name: 'trend', title: 'Status', alignment: 'center' }
        ]
      });
      
      statsTable.addRow({
        metric: '✅ Completed Tasks',
        value: completedToday.toString(),
        trend: completedToday > 0 ? '🎉' : '⏳'
      });
      
      statsTable.addRow({
        metric: '⏱️  Time Spent',
        value: formatDuration(totalTimeToday),
        trend: totalTimeToday > 240 ? '💪' : '📈'
      });
      
      statsTable.addRow({
        metric: '📋 Pending Tasks',
        value: pendingTasks.length.toString(),
        trend: pendingTasks.length > 10 ? '⚠️' : '✅'
      });
      
      statsTable.addRow({
        metric: '🔥 High Priority',
        value: highPriorityPending.toString(),
        trend: highPriorityPending > 0 ? '🚨' : '✅'
      });
      
      statsTable.printTable();
      
      // 当前任务
      if (state?.current) {
        console.log(colors.header('\n🔄 Current Task:'));
        console.log(colors.cyan(state.current.task));
        const currentDuration = Date.now() - new Date(state.current.startTime).getTime();
        console.log(colors.dim(`Running for ${formatDuration(currentDuration)}`));
      }
      
      // 详细任务列表
      if (options.verbose && todayTasks.length > 0) {
        console.log(colors.header('\n📝 Today\'s Completed Tasks:'));
        
        todayTasks
          .filter(t => t.status === 'completed')
          .forEach((task, index) => {
            console.log(`${index + 1}. ${task.task}`);
            console.log(colors.dim(`   Duration: ${formatDuration(task.duration)} | Completed: ${new Date(task.endTime).toLocaleTimeString()}`));
            if (task.notes) {
              console.log(colors.dim(`   Notes: ${task.notes}`));
            }
          });
      }
      
      // 生产力评分
      const productivityScore = calculateProductivityScore(completedToday, totalTimeToday, highPriorityPending);
      console.log(colors.header('\n🎯 Productivity Score:'));
      console.log(getProductivityBar(productivityScore));
      console.log(getProductivityMessage(productivityScore));
      
      // 建议
      if (pendingTasks.length > 0) {
        console.log(colors.header('\n💡 Suggestions:'));
        if (highPriorityPending > 0) {
          console.log(`• Focus on ${highPriorityPending} high-priority tasks`);
        }
        if (completedToday === 0) {
          console.log('• Start with a simple task to build momentum');
        }
        if (totalTimeToday > 480 * 60 * 1000) { // 8 hours in milliseconds
          console.log('• Consider taking a break, you\'ve worked hard today!');
        }
      }
    }));
  
  return command;
}

/**
 * 创建 quick 命令 - 交互式快速操作菜单
 */
export function createQuickCommand(): Command {
  const command = new Command('quick');
  
  command
    .description('Interactive quick action menu')
    .alias('q')
    .action(withErrorHandling(async () => {
      const manager = new WorkflowManager();
      const taskStore = new TaskStore();
      await taskStore.initialize();
      
      const state = await manager.getTaskState();
      
      // 构建选项
      const choices = [];
      
      if (state?.current) {
        choices.push(
          { name: '✅ Complete current task', value: 'done' },
          { name: '📊 Show current task status', value: 'now' },
          { name: '⏭️  Skip to next task', value: 'skip' }
        );
      } else {
        choices.push(
          { name: '▶️  Start next task', value: 'next' }
        );
      }
      
      choices.push(
        { name: '📋 List all tasks', value: 'list' },
        { name: '🏷️  Manage tags', value: 'tags' },
        { name: '📊 Today\'s summary', value: 'today' },
        { name: '🔍 Search tasks', value: 'search' },
        { name: '➕ Create new task', value: 'create' },
        { name: '❌ Exit', value: 'exit' }
      );
      
      const { action } = await inquirer.prompt([{
        type: 'list',
        name: 'action',
        message: 'What would you like to do?',
        choices
      }]);
      
      // 执行操作
      switch (action) {
        case 'done':
          await createDoneCommand().parseAsync(['done'], { from: 'user' });
          break;
          
        case 'now':
          await createNowCommand().parseAsync(['now', '-d'], { from: 'user' });
          break;
          
        case 'next':
        case 'skip':
          await createNextCommand().parseAsync(['next'], { from: 'user' });
          break;
          
        case 'list':
          const tasks = await taskStore.getTasks();
          const pendingTasks = tasks.filter(t => t.status === 'pending');
          
          if (pendingTasks.length === 0) {
            console.log(colors.success('🎉 No pending tasks!'));
          } else {
            console.log(colors.header(`\n📋 Pending Tasks (${pendingTasks.length}):\n`));
            pendingTasks.slice(0, 10).forEach((task, index) => {
              const priority = task.priority === 'high' ? '🔴' : task.priority === 'medium' ? '🟡' : '🟢';
              console.log(`${index + 1}. ${priority} ${task.content}`);
              if (task.tags && task.tags.length > 0) {
                console.log(colors.dim(`   Tags: ${task.tags.join(', ')}`));
              }
            });
          }
          break;
          
        case 'tags':
          const tagChoices = [
            { name: 'View all tags', value: 'list' },
            { name: 'Add tags to task', value: 'add' },
            { name: 'Filter by tags', value: 'filter' },
            { name: 'Back', value: 'back' }
          ];
          
          const { tagAction } = await inquirer.prompt([{
            type: 'list',
            name: 'tagAction',
            message: 'Tag management:',
            choices: tagChoices
          }]);
          
          if (tagAction === 'list') {
            const { createTagsCommand } = await import('./tags');
            await createTagsCommand().parseAsync(['tags', 'list'], { from: 'user' });
          }
          break;
          
        case 'today':
          await createTodayCommand().parseAsync(['today'], { from: 'user' });
          break;
          
        case 'search':
          const { query } = await inquirer.prompt([{
            type: 'input',
            name: 'query',
            message: 'Search tasks:'
          }]);
          
          if (query) {
            const tasks = await taskStore.searchTasks(query);
            console.log(colors.header(`\n🔍 Search Results (${tasks.length}):\n`));
            tasks.forEach((task, index) => {
              console.log(`${index + 1}. ${task.content}`);
            });
          }
          break;
          
        case 'create':
          const { content, priority, tags } = await inquirer.prompt([
            {
              type: 'input',
              name: 'content',
              message: 'Task description:',
              validate: input => input.length > 0 || 'Task description is required'
            },
            {
              type: 'list',
              name: 'priority',
              message: 'Priority:',
              choices: ['high', 'medium', 'low'],
              default: 'medium'
            },
            {
              type: 'input',
              name: 'tags',
              message: 'Tags (comma-separated, optional):'
            }
          ]);
          
          const newTask = await taskStore.addTask({
            content,
            priority: priority as TaskPriority,
            tags: tags ? tags.split(',').map((t: string) => t.trim()) : []
          });
          
          console.log(colors.success('✅ Task created successfully!'));
          break;
      }
    }));
  
  return command;
}

// 辅助函数

function getComplexityColor(score: number): string {
  if (score > 70) return colors.red(`${score}`);
  if (score > 50) return colors.yellow(`${score}`);
  return colors.green(`${score}`);
}

function calculateProductivityScore(completed: number, timeSpent: number, highPriority: number): number {
  let score = 0;
  
  // 基于完成数量（最高 40 分）
  score += Math.min(40, completed * 10);
  
  // 基于时间效率（最高 30 分）
  if (timeSpent > 0) {
    const efficiency = completed / (timeSpent / 3600000); // 任务/小时（timeSpent是毫秒）
    score += Math.min(30, efficiency * 20);
  }
  
  // 基于优先级处理（最高 30 分）
  if (highPriority === 0) {
    score += 30;
  } else if (completed > 0) {
    score += Math.max(0, 30 - highPriority * 10);
  }
  
  return Math.round(score);
}

function getProductivityBar(score: number): string {
  const filled = Math.round(score / 10);
  const empty = 10 - filled;
  const bar = '█'.repeat(filled) + '░'.repeat(empty);
  
  let color = colors.green;
  if (score < 40) color = colors.red;
  else if (score < 70) color = colors.yellow;
  
  return color(bar) + ` ${score}/100`;
}

function getProductivityMessage(score: number): string {
  if (score >= 80) return colors.success('🌟 Excellent work! Keep it up!');
  if (score >= 60) return colors.success('👍 Good progress today!');
  if (score >= 40) return colors.warning('💪 You can do better!');
  return colors.dim('🚀 Let\'s get started!');
}